/**
 * Unsaved-changes guard — central registry + centered modal that warns the
 * operator before navigating away from a screen with staged/edited work that
 * isn't persisted yet.
 *
 * Mechanics:
 *   1. Any component with in-flight state calls `useUnsavedChanges(isDirty, label)`.
 *      As long as `isDirty` is true, that component is in the registry; when
 *      it flips false (or the component unmounts), it leaves.
 *   2. Router-level navigation (Header nav links, in-app <Link>s, back/forward)
 *      is intercepted by <NavBlocker/> via react-router's `useBlocker` — when
 *      the registry is non-empty it opens the modal instead of navigating.
 *   3. Legacy in-component nav setters (a section's own sub-tab pills that still
 *      call `setState` rather than navigating) route their intent through
 *      `useNavGuard()` → `guard(action)`. As those sections migrate to real
 *      routes their pills become <NavLink>s and fall under NavBlocker instead;
 *      `guard` stays as the seam until the last one is converted.
 *   4. Either path opens the same modal (list of dirty surfaces, "Stay" /
 *      "Discard & continue"). Discard proceeds; Stay dismisses.
 *   5. Discard doesn't actively clear component state — it just acknowledges
 *      the loss. The destination view's render unmounts the dirty component, at
 *      which point each `useUnsavedChanges` cleanup unregisters.
 *
 * Browser-level navigation (close tab / hard refresh) is NOT covered here on
 * purpose — the native `beforeunload` dialog is unstyled and varies per
 * browser, which the operator explicitly didn't want. (In-app back/forward IS
 * now covered, via useBlocker.) Add a beforeunload listener later if that changes.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker, type BlockerFunction } from "react-router-dom";

interface UnsavedChangesContextValue {
  /** Register / unregister a dirty surface. Components call this via the hook. */
  register: (id: string, label: string) => void;
  unregister: (id: string) => void;
  /**
   * Run `action` if nothing's dirty; otherwise open the modal and queue the
   * action for "Discard & continue." Wrap non-router nav setters with this —
   * e.g. `guard(() => setSubTab("mappings"))`.
   */
  guard: (action: () => void) => void;
}

// Read-only view of the registry, in its own context so `useNavGuard` consumers
// (whose context value must stay identity-stable) don't re-render on every
// dirty-state change — only the components that actually read count/labels do.
interface UnsavedChangesState {
  count: number;
  labels: string[];
}

const Ctx = createContext<UnsavedChangesContextValue | null>(null);
const StateCtx = createContext<UnsavedChangesState>({ count: 0, labels: [] });

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Registry is a ref (not state) so register/unregister calls during a
  // render's useEffect don't trigger their own re-renders. We tick the
  // version state when the registry changes so consumers see the new labels.
  const registry = useRef<Map<string, string>>(new Map());
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  // Queued action when the modal is open (the legacy `guard` path).
  const [pending, setPending] = useState<{ run: () => void } | null>(null);

  const register = useCallback(
    (id: string, label: string) => {
      if (registry.current.get(id) === label) return; // no-op
      registry.current.set(id, label);
      bump();
    },
    [bump],
  );
  const unregister = useCallback(
    (id: string) => {
      if (!registry.current.has(id)) return;
      registry.current.delete(id);
      bump();
    },
    [bump],
  );
  const guard = useCallback((action: () => void) => {
    if (registry.current.size === 0) {
      action();
      return;
    }
    // Wrap in { run } so the setter doesn't try to invoke the function (React
    // setState supports functional updates — `setPending(action)` would call
    // `action(prev)` instead of storing it).
    setPending({ run: action });
  }, []);

  const value = useMemo<UnsavedChangesContextValue>(
    () => ({ register, unregister, guard }),
    [register, unregister, guard],
  );

  // `version` is intentionally a dep so labels refresh after register/unregister
  // mutations to the ref. Lints are happy with the explicit reference.
  const labels = useMemo(() => Array.from(registry.current.values()), [version]);
  const stateValue = useMemo<UnsavedChangesState>(
    () => ({ count: labels.length, labels }),
    [labels],
  );

  const onStay = () => setPending(null);
  const onDiscard = () => {
    const p = pending;
    setPending(null);
    if (p) p.run();
  };

  return (
    <Ctx.Provider value={value}>
      <StateCtx.Provider value={stateValue}>
        {children}
        {pending && <UnsavedChangesModal labels={labels} onStay={onStay} onDiscard={onDiscard} />}
      </StateCtx.Provider>
    </Ctx.Provider>
  );
};

/**
 * Declare a dirty surface. Whenever `isDirty` is true the modal will fire on
 * any nav intent (router navigation via NavBlocker, or a `guard`ed setter).
 * `label` is what the operator sees in the modal's bullet list — keep it short
 * and recognizable (e.g. "Roll Forwards preview"). Pass a stable `id` if
 * multiple instances of the same component can mount; the default uses an
 * autogenerated key.
 */
export const useUnsavedChanges = (isDirty: boolean, label: string, id?: string) => {
  const ctx = useContext(Ctx);
  // Stable per-instance id when none provided.
  const fallbackIdRef = useRef<string>();
  if (!fallbackIdRef.current) {
    fallbackIdRef.current = `uc-${Math.random().toString(36).slice(2, 10)}`;
  }
  const myId = id || fallbackIdRef.current;
  useEffect(() => {
    if (!ctx) return;
    if (isDirty) ctx.register(myId, label);
    else ctx.unregister(myId);
    return () => {
      ctx.unregister(myId);
    };
  }, [isDirty, label, ctx, myId]);
};

/**
 * Use from a NON-router nav-firing surface (a section's own sub-tab pills that
 * still `setState` instead of navigating). Pass it a thunk that performs the
 * navigation; it'll either run immediately or open the modal first. When no
 * provider is mounted (e.g. unit tests), returns an identity that just runs the
 * action. Router navigation does NOT need this — <NavBlocker/> covers it.
 */
export const useNavGuard = (): ((action: () => void) => void) => {
  const ctx = useContext(Ctx);
  return ctx ? ctx.guard : (action: () => void) => action();
};

/** Read the current dirty-surface count/labels (for NavBlocker + any UI badge). */
export const useUnsavedState = (): UnsavedChangesState => useContext(StateCtx);

/**
 * `navigate()` options that tell <NavBlocker/> to let THIS navigation through
 * even while a dirty surface is registered. Use ONLY for an intended
 * post-SAVE navigation — the canonical case is the switch to Committed Orders
 * right after a successful commit: the staged grid is still "dirty" for the
 * one render before it unmounts, but the work is already persisted, so the
 * unsaved-changes modal would be a false alarm. Pass as the 2nd arg:
 *   navigate("/trading-desk/trade/committed", SKIP_UNSAVED_GUARD)
 * The flag rides in that navigation's history state, so it's scoped to exactly
 * this one transition — pills / nav links / back-forward stay guarded.
 */
export const SKIP_UNSAVED_GUARD = { state: { __allowUnsaved: true } } as const;

/**
 * Router navigation guard. Mount ONCE inside the RouterProvider tree (it lives
 * in RootLayout). Uses react-router's `useBlocker` to intercept any in-app
 * navigation — nav links, <Link>s, redirects, AND the browser back/forward
 * button — whenever a dirty surface is registered, opening the same modal.
 * A navigation explicitly flagged with SKIP_UNSAVED_GUARD is let through (see
 * that export) so an intended post-save switch doesn't trip its own guard.
 */
export const NavBlocker: React.FC = () => {
  const { count, labels } = useUnsavedState();
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ nextLocation }) =>
        count > 0 &&
        !(nextLocation.state as { __allowUnsaved?: boolean } | null)?.__allowUnsaved,
      [count],
    ),
  );

  // Escape = Stay (the safer default) while the block modal is open.
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") blocker.reset?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [blocker]);

  if (blocker.state !== "blocked") return null;
  return (
    <UnsavedChangesModal
      labels={labels}
      onStay={() => blocker.reset?.()}
      onDiscard={() => blocker.proceed?.()}
    />
  );
};

// ─────────────────────────────────────── modal ── //

const UnsavedChangesModal: React.FC<{
  labels: string[];
  onStay: () => void;
  onDiscard: () => void;
}> = ({ labels, onStay, onDiscard }) => {
  // Close on Escape (= Stay — the safer default).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onStay();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onStay]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-changes-title"
      onClick={onStay}
    >
      <div
        className="w-full max-w-md rounded-lg bg-surface-overlay shadow-2xl border border-line p-5 m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="unsaved-changes-title"
          className="font-montserrat text-base font-bold text-content mb-2"
        >
          Unsaved changes
        </h2>
        <p className="text-sm text-content-subtle">
          You have in-flight work that won't be saved if you navigate away:
        </p>
        <ul className="mt-2 text-sm text-content list-disc list-inside space-y-0.5">
          {labels.length > 0 ? (
            labels.map((l) => <li key={l}>{l}</li>)
          ) : (
            <li>Unsaved changes.</li>
          )}
        </ul>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onDiscard}
            className="px-3 py-1.5 text-sm text-content-subtle hover:text-status-bad hover:underline transition-colors"
          >
            Discard &amp; continue
          </button>
          <button
            onClick={onStay}
            autoFocus
            className="px-4 py-1.5 text-sm font-semibold bg-qw-teal text-white rounded hover:bg-qw-teal/90 transition-colors"
          >
            Stay
          </button>
        </div>
      </div>
    </div>
  );
};
